import type { CampaignAnalyticsGranularity, PlacementKey } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PerformancePanel } from "@/components/shared/performance/PerformancePanel";
import {
  DEFAULT_PERFORMANCE_SLOTS,
  PERFORMANCE_METRICS,
  type PerformanceMetric,
} from "@/components/shared/performance/performance";
import { Dialog, DialogHeader, DialogPopup, DialogTitle } from "@/components/ui/dialog";
import { fetchPlacementTrend } from "@/lib/campaign-api";
import { bucketPoints, kpisOf } from "./bid-adjustment-model";

export interface PlacementDetailDialogProps {
  campaignId: string;
  open: boolean;
  /** The placement to show; kept after closing so the exit animation keeps its content. */
  placement: PlacementKey | null;
  name: string;
  dates: { startDate: string; endDate: string };
  onClose: () => void;
}

/** 数据详情: one placement's KPI cards and trend over the tab's window. */
export function PlacementDetailDialog({
  campaignId,
  open,
  placement,
  name,
  dates,
  onClose,
}: PlacementDetailDialogProps) {
  const { t } = useTranslation("campaign");
  const [granularity, setGranularity] = useState<CampaignAnalyticsGranularity>("day");
  const [metric, setMetric] = useState<PerformanceMetric>("spend");
  const [compareMetric, setCompareMetric] = useState<PerformanceMetric | null>("sales");
  const query = useQuery({
    queryKey: [
      "campaign",
      campaignId,
      "placement-trend",
      placement,
      dates.startDate,
      dates.endDate,
    ],
    queryFn: ({ signal }) =>
      fetchPlacementTrend(campaignId, placement as PlacementKey, dates, signal),
    enabled: placement !== null,
    staleTime: 60_000,
  });
  const points = query.data?.data.points ?? [];
  const currency = query.data?.data.currency ?? null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogPopup className="w-[min(1100px,95vw)] max-w-[95vw]">
        <DialogHeader>
          <DialogTitle>{name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[80vh] overflow-auto px-6 pb-6">
          <PerformancePanel
            showPreviousValue={false}
            metrics={PERFORMANCE_METRICS}
            defaultSlots={DEFAULT_PERFORMANCE_SLOTS}
            kpis={query.data ? kpisOf(points, currency) : []}
            trend={bucketPoints(points, granularity)}
            dataThrough={null}
            metric={metric}
            onMetricChange={setMetric}
            compareMetric={compareMetric}
            onCompareMetricChange={setCompareMetric}
            granularity={granularity}
            onGranularityChange={setGranularity}
            isLoading={query.isPending}
            isFetching={query.isFetching}
            isError={query.isError}
            onRetry={() => void query.refetch()}
            title={t("detail.bidAdjustment.detail")}
            subtitle={`${dates.startDate} ~ ${dates.endDate}`}
            getMetricLabel={(key) => t(`detail.performance.metrics.${key}`)}
            collapseStorageKey="adpilot:placement-detail-chart:v1"
            slotsStorageKey="adpilot:placement-detail-slots:v1"
          />
        </div>
      </DialogPopup>
    </Dialog>
  );
}
